/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/

import { TableCellItem } from '@cedalo/gateway';
import { Reference } from '@cedalo/parser';
import { Box, Button, TextField, Tooltip, Typography } from '@mui/material';
import format from 'date-fns/format';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { IconIdea } from '../../../components/icons';
import theme from '../../../theme';
import { withAppData } from '../../app/AppProvider';
import { runQuery, useLocalState, useQuery, useSubscription } from '../../app/GraphQLWSClient';
import { Form, WIDTH } from '../../utils/Form';
import SettingsPaper from '../../utils/SettingsPaper';
import { setHash } from '../../utils/URLHash';
import CLEAR_CONNECTION_LOGS_MUTATION from './clear-connection-logs.gql';
import CONNECTION_FORM_ACTION_MUTATION from './connection-form-action.gql';
import INBOX_FORM_UPDATES_SUBSCRIPTION from './connection-inbox-form-updates.gql';
import INBOX_FORM_QUERY from './connection-inbox-form.gql';
import CONNECTION_LOGS_SUBSCRIPTION from './connection-logs-subscription.gql';
import CONNECTION_UPDATES_SUBSCRIPTION from './connection-updates.gql';
import CONNECTION_QUERY from './connection.gql';
import { ConnectionIcon } from './ConnectionIcon';
import { LearnMoreDialog } from './LearnMoreDialog';
import { StatusDot } from './StatusDot';
// import DELETE_CONNECTION_MUTATION from './delete-connection.gql';
import UPDATE_NAME_MUTATION from './update-connection-name.gql';

const formActionCreator = (activeSheet: string, connectionId: string, formType: string) => async (type, id, value) => {
	// console.log('formAction', connectionId, id, value);
	const result = await runQuery(CONNECTION_FORM_ACTION_MUTATION, {
		input: { activeSheet, connectionId, actionType: type, actionData: { id, value }, form: formType }
	});
	return result;
};

const formatLogDate = (string) => format(new Date(string), 'yyyy-MM-dd HH:mm:ss');

const logTableFormActionCreator = (connectionId: string) => async (type, id) => {
	if (type === 'click' && id === 'clear') {
		await runQuery(CLEAR_CONNECTION_LOGS_MUTATION, { connectionId });
	}
};

type LogMessage = { timestamp: string; message: string; isError: boolean; id: string };

const LogsTable = ({ logs = [], connectionId }: { logs: LogMessage[]; connectionId: string }) => (
	<Form
		formActionCreator={() => logTableFormActionCreator(connectionId)}
		formType="log_table"
		layout="1fr"
		items={[
			// {
			// 	type: 'header',
			// 	value: 'Logs'
			// },
			{
				type: 'table',
				layout: '2fr 4fr 32px',
				items: [
					{ type: 'header', key: 'timestamp-header', value: 'Timestamp' },
					{ type: 'header', key: 'message-header', value: 'Message' },
					logs.length
						? { type: 'button', icon: 'delete', actionId: 'clear', key: 'clear-button' }
						: { type: 'header', value: '', key: 'clear-button' },
					...(logs.flatMap(({ timestamp, message, isError, id }) => [
						{
							type: isError ? 'error' : 'text',
							value: formatLogDate(timestamp),
							key: `e.${id}.ts`
						},
						{
							type: isError ? 'error' : 'text',
							value: message,
							key: `e.${id}.msg`,
							location: 'auto / 2 / auto / span 2'
						}
					]) as TableCellItem[])
				]
			}
		]}
	/>
);

export const AddConnection = withAppData(({ connections }) => ({ connections }))(({ targetId, connections }) => {
	const available = connections
		.filter(({ targets, canHaveTargets }) => canHaveTargets && !targets.includes(targetId))
		.sort((a, b) => (a.name < b.name ? -1 : 1));
	return available.length ? (
		<Form
			formActionCreator={(formType: string) =>
				async (type, id, { connectionId, ...actionData }) =>
					formActionCreator(targetId, connectionId, formType)(type, id, actionData)}
			formType="add_connection"
			layout="1fr"
			items={[
				{
					type: 'table',
					layout: '1fr 32px',
					items: [
						{
							type: 'header',
							value: (
								<FormattedMessage
									id="InboxSettings.AddExistingConnection"
									defaultMessage="Add Connection"
								/>
							),
							location: 'auto / 1 / auto / span 2',
							style: {
								backgroundColor: theme.palette.primary.main,
								color: theme.palette.primary.contrastText,
								textTransform: 'uppercase'
							}
						},

						...available.flatMap(({ id, name }) => [
							{
								type: 'text',
								value: name,
								key: id
							},
							{ type: 'button', icon: 'add', actionId: 'targets.add', actionData: { connectionId: id } }
						])
						// { type: 'link', icon: 'add', href: '/Connections/new', location: 'auto / 1 / auto / span 2' }
					]
				}
			]}
		/>
	) : null;
});

export const ConnectionDetailView = withAppData(({ connections }, { connectionId }) => {
	const connection = connections.find((c) => c.id === connectionId);
	return {
		hasError: !connection?.status?.isConnected
	};
})(function ConnectionDetailViewComponent({ activeSheet, connectionId, onClose, history, hasError }) {
	const [form, setForm] = useState(null);
	const [logs, setLogs] = useState([]);
	const [error, setError] = useState(null);
	const [hasBeenDeleted /* setHasBeenDeleted */] = useState(false);
	// const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);
	const [name, setLocalName, setRemoteName, isChanged] = useLocalState(null);
	const [nameError, setNameError] = useLocalState(null);
	const [connectionModule, setConnectionModule] = useState(null);
	const [learnMoreDialogOpen, setLearnMoreDialogOpen] = useState(false);
	// const navigate = useNavigate();

	const connectionQueryResult = useQuery(CONNECTION_QUERY, { connectionId });
	useEffect(() => {
		const { status, result } = connectionQueryResult;
		if (status === 'done' && result.connection) {
			if (!form) {
				setForm(result.connection.form);
				setRemoteName(result.connection.name);
				setLogs(result.connection.logs);
				setConnectionModule(result.connection.module);
			}
		} else if (status === 'error') {
			console.error(result);
			setError(JSON.stringify(result));
		}
	}, [connectionQueryResult]);

	const [subData, subError] = useSubscription(CONNECTION_UPDATES_SUBSCRIPTION, { connectionId });
	const [logSubData, logSubError] = useSubscription(CONNECTION_LOGS_SUBSCRIPTION, { connectionId });

	useEffect(() => {
		if (subError) {
			console.log(subError);
			setError(JSON.stringify(subError));
		}
	}, [subError]);
	useEffect(() => {
		if (subData && subData.connectionUpdated.form) {
			const newForm = subData.connectionUpdated.form;
			setRemoteName(subData.connectionUpdated.name);
			setForm(newForm);
		}
	}, [subData]);

	useEffect(() => {
		if (logSubError) {
			console.error(logSubError);
			setError(JSON.stringify(logSubError));
		}
	}, [logSubError]);
	useEffect(() => {
		const data = logSubData && logSubData.connectionLogsChanged;
		if (data) {
			if (data.__typename === 'NewConnectionLogEvent') {
				setLogs((current) => [data.log, ...current]);
			} else {
				setLogs([]);
			}
		}
	}, [logSubData]);
	if (hasBeenDeleted) {
		return (
			<div
				style={{
					width: '100%',
					height: '100%',
					display: 'flex'
				}}
			>
				This Connection is no longer available!
			</div>
		);
	}

	// const onDelete = async () => {
	// 	try {
	// 		const result = await runQuery(DELETE_CONNECTION_MUTATION, { connectionId });
	// 		if (!result.success) {
	// 			console.error(result.message);
	// 		}
	// 	} catch (e) {
	// 		console.error(e);
	// 	}
	// 	onClose();
	// };

	const setName = async (newName) => {
		if (!name) {
			return;
		}
		try {
			const { updateConnectionName } = await runQuery(UPDATE_NAME_MUTATION, { connectionId, name: newName });
			if (updateConnectionName.success) {
				setRemoteName(updateConnectionName.name);
				setLocalName((current) => (current === updateConnectionName.name ? null : current));
				setNameError(null);
			}
			if (updateConnectionName.code === 'CONFLICT') {
				setNameError('Another connection or a sheet already uses this name!');
			}
		} catch (e) {
			console.error(e);
		}
	};

	const onBack = () => {
		window.location.hash = history;
	};

	return (
		// @ts-ignore
		<SettingsPaper
			open
			helpContext="connectionsettings"
			close={() => onClose()}
			back={() => onBack()}
			// @ts-ignore
			style={{ width: '600px' }}
			title={
				history && history.indexOf('Inbox') !== -1 ? (
					<FormattedMessage id="ConnectionView.TitleInbox" defaultMessage="Inbox Connection" />
				) : (
					<FormattedMessage id="ConnectionView.Title" defaultMessage="Connection" />
				)
			}
		>
			{error ? <div>ERROR: {JSON.stringify(error, null, 2)}</div> : null}
			{form ? (
				<Box>
					<Box
						sx={{
							display: 'grid',
							gridGap: '8px',
							gridTemplateColumns: '24px auto 24px 1fr',
							alignItems: 'center'
							// alignContent: 'center'
						}}
					>
						<ConnectionIcon src={connectionModule.logo} />
						<Typography color="text.primary" variant="h6" sx={{ lineHeight: 'normal' }}>
							{connectionModule.name}
						</Typography>

						<StatusDot
							style={{
								cursor: 'pointer'
							}}
							status={hasError ? 'disconnected' : 'connected'}
							onClick={() => setHash({ tab: 'logs' })}
						/>
						{connectionModule.about || connectionModule.useCases ? (
							<Tooltip
								enterDelay={300}
								title={
									<FormattedMessage
										id="Tooltip.ConnectionLearnMore"
										defaultMessage="Learn  more about {type}"
										values={{
											type: connectionModule.name
										}}
									/>
								}
							>
								<Button
									sx={{ justifySelf: 'end', padding: '6px 8px 6px 10px', minWidth: '0px' }}
									// color="primary"
									variant="contained"
									// startIcon={<IconIdea />}
									onClick={() => setLearnMoreDialogOpen(true)}
								>
									<IconIdea />
								</Button>
							</Tooltip>
						) : null}

						<LearnMoreDialog
							open={learnMoreDialogOpen}
							onClose={() => setLearnMoreDialogOpen(false)}
							{...connectionModule}
						/>
					</Box>
					<TextField
						value={name}
						label={<FormattedMessage id="Name" defaultMessage="Name" />}
						style={{
							marginBottom: '16px',
							flex: '1',
							maxWidth: WIDTH.medium
						}}
						fullWidth
						variant="outlined"
						size="small"
						margin="normal"
						inputProps={{ autocomplete: 'false' }}
						error={!!nameError}
						helperText={nameError}
						onChange={(event) => {
							setLocalName(event.target.value);
							if (!Reference.isValidIdentifier(event.target.value)) {
								setNameError(
									<FormattedMessage
										id="Reference.InvalidName"
										defaultMessage="Only alphanumeric characters and the underscore are allowed"
									/>
								);
							} else {
								setNameError(null);
							}
						}}
						onKeyUp={(e) => {
							if (e.key === 'Escape') {
								setLocalName(null);
								setNameError(null);
							} else if (e.key === 'Enter') {
								if (!name) {
									return;
								}
								if (isChanged) {
									setName(name);
								} else {
									setNameError(null);
								}
							}
						}}
						onBlur={() => {
							if (!name) {
								setLocalName(null);
							} else if (isChanged) {
								setName(name);
							} else {
								setNameError(null);
							}
						}}
					/>

					<Form
						{...form}
						formType="main"
						formActionCreator={(formType: string) => formActionCreator(activeSheet, connectionId, formType)}
						createCustomElement={({ element, ...item }) => {
							switch (element) {
								case 'logtable':
									return <LogsTable logs={logs} connectionId={connectionId} {...item} />;
								default:
									return null;
							}
						}}
					/>
				</Box>
			) : null}
		</SettingsPaper>
	);
});

const connectionFormSorter = (a, b) => (a.items[0].items[0].value < b.items[0].items[0].value ? -1 : 1);

export const InboxConnectionSettings = ({ targetId, machineId }) => {
	const [forms, setForms] = useState(null);
	const connectionQueryResult = useQuery(INBOX_FORM_QUERY, { targetId, machineId });
	useEffect(() => {
		const { status, result } = connectionQueryResult;
		if (status === 'done' && result && result.machine) {
			// if (!forms) {
			setForms(result.machine.inboxConnectionForms.sort(connectionFormSorter));
			// }
		} else if (status === 'error') {
			console.error(result);
		}
	}, [connectionQueryResult]);

	const [subData, subError] = useSubscription(INBOX_FORM_UPDATES_SUBSCRIPTION, { targetId, machineId });

	useEffect(() => {
		if (subError) {
			console.error(subError);
		}
	}, [subError]);
	useEffect(() => {
		if (subData && subData.connectionUpdated) {
			const { inboxForm: newForm, connectionId } = subData.connectionUpdated;
			setForms((current) => {
				if (!newForm) {
					return current.filter((form) => form.connectionId !== connectionId);
				}
				if (current.find((form) => form.connectionId === connectionId)) {
					return current
						.map((form) => (form.connectionId === connectionId ? newForm : form))
						.sort(connectionFormSorter);
				}
				return [...current, newForm].sort(connectionFormSorter);
			});
		}
	}, [subData]);
	return (
		forms && [
			...forms.map((form) => (
				<Form
					{...form}
					formType="inbox"
					formActionCreator={(formType: string) => formActionCreator(targetId, form.connectionId, formType)}
					style={{ marginBottom: '8px' }}
				/>
			)),
			<AddConnection targetId={targetId} />
		]
	);
};
